// Small pieces shared by Register updates' overview and review pages.
// docs/core/operations/REGISTER_UPDATES.md

import type { ReactNode } from 'react';
import { AppIcon } from '../changelog/AppIcon';
import type { ChangeKind, ChangeStatus, RegisterChange, RegisterMerge, RegisterRelease } from '../data/register';

/** A change's place in the flow, as the glyph in a circle beside it. */
export function StatusGlyph({ status, current = false, size = 18 }: { status: ChangeStatus; current?: boolean; size?: number }) {
  const box = { width: size, height: size };
  if (status === 'accepted' || status === 'applied' || status === 'rejected') {
    const rejected = status === 'rejected';
    return (
      <span
        aria-hidden="true"
        style={box}
        className={`inline-flex flex-none items-center justify-center rounded-full text-[13px] font-bold ${rejected ? 'bg-rose-400/15 text-rose-300' : 'bg-emerald-400/15 text-emerald-400'}`}
      >
        {rejected ? '×' : '✓'}
      </span>
    );
  }
  if (status === 'conflict') {
    return <span aria-hidden="true" style={box} className="inline-flex flex-none items-center justify-center rounded-full bg-amber-400/15 text-[13px] font-bold text-amber-400">!</span>;
  }
  return (
    <span
      aria-hidden="true"
      style={box}
      className={`inline-flex flex-none rounded-full ${current ? 'shadow-[inset_0_0_0_2px_#22d3ee]' : 'shadow-[inset_0_0_0_1.5px_#475569]'}`}
    />
  );
}

/** What the status means, in words, for screen readers and the list's subtitle. */
export const STATUS_WORDS: Record<ChangeStatus, string> = {
  open: 'To check',
  conflict: 'Look again',
  accepted: 'Accepted',
  rejected: 'Kept as it was',
  applied: 'In the register',
};

const KIND: Record<ChangeKind, { label: string; cls: string }> = {
  update: { label: 'Update', cls: 'bg-cyan-400/10 text-cyan-300' },
  retire: { label: 'Retire', cls: 'bg-rose-400/10 text-rose-300' },
  new: { label: 'New row', cls: 'bg-purple-400/15 text-purple-300' },
};

export function KindPill({ kind, conflict = false }: { kind: ChangeKind; conflict?: boolean }) {
  const k = conflict ? { label: 'Look again', cls: 'bg-amber-400/15 text-amber-300' } : KIND[kind];
  return <span className={`rounded-full px-2.5 py-0.5 text-[13px] font-semibold ${k.cls}`}>{k.label}</span>;
}

/** A small tag on a list row for retirements and new rows. */
export function KindTag({ kind }: { kind: ChangeKind }) {
  if (kind === 'update') return null;
  return <span className={`flex-none rounded-md px-1.5 py-0.5 text-[13px] font-semibold ${KIND[kind].cls}`}>{KIND[kind].label}</span>;
}

const SEGMENT: Record<ChangeStatus, string> = {
  applied: 'bg-emerald-400',
  accepted: 'bg-emerald-400',
  rejected: 'bg-emerald-400/60',
  conflict: 'bg-amber-400',
  open: 'bg-slate-400/25',
};

/** One segment per change: done first, then look again, then to check; `currentId` lit cyan. */
export function ProgressSegments({ changes, currentId, className = '' }: { changes: RegisterChange[]; currentId?: string; className?: string }) {
  const order: ChangeStatus[] = ['applied', 'accepted', 'rejected', 'conflict', 'open'];
  const sorted = [...changes].sort((a, b) => order.indexOf(a.status) - order.indexOf(b.status));
  return (
    <div className={`flex h-1.5 gap-[3px] ${className}`} aria-hidden="true">
      {sorted.map((c) => (
        <span key={c.id} className={`flex-1 rounded-sm ${c.id === currentId ? 'bg-cyan-400' : SEGMENT[c.status]}`} />
      ))}
    </div>
  );
}

/** A keyboard hint. */
export function Kbd({ children, onAccent = false }: { children: ReactNode; onAccent?: boolean }) {
  return (
    <kbd
      className={`inline-flex h-5 min-w-5 items-center justify-center rounded-[5px] border px-1 font-mono text-[13px] font-medium leading-none ${
        onAccent ? 'border-white/35 bg-white/10 text-white' : 'border-slate-400/25 bg-slate-400/10 text-slate-400'
      }`}
    >
      {children}
    </kbd>
  );
}

/** "Home 2.54.0" with its app tile. */
export function ReleaseName({ release, size = 26, className = '' }: { release: RegisterRelease; size?: number; className?: string }) {
  return (
    <span className={`inline-flex items-center gap-2.5 ${className}`}>
      <AppIcon app={release.app} size={size} />
      <span>{release.module} {release.version}</span>
    </span>
  );
}

/** Changes grouped by where they are on the Scout, in the order the screens first appear. */
export function byScreen(changes: RegisterChange[]): { screen: string; changes: RegisterChange[] }[] {
  const groups = new Map<string, RegisterChange[]>();
  for (const c of changes) {
    const g = groups.get(c.screen);
    if (g) g.push(c);
    else groups.set(c.screen, [c]);
  }
  return [...groups].map(([screen, list]) => ({ screen, changes: list }));
}

export interface MergeGroup {
  /** Null for changes drafted with a whole release, before per-merge drafting. */
  merge: RegisterMerge | null;
  changes: RegisterChange[];
}

/** Changes grouped by the merge that drafted them, in merge order; changes with no merge last. */
export function byMerge(changes: RegisterChange[], merges: RegisterMerge[]): MergeGroup[] {
  const groups: MergeGroup[] = [...merges].sort((a, b) => a.position - b.position).map((merge) => ({ merge, changes: [] }));
  const at = new Map(groups.map((g) => [g.merge!.id, g]));
  const loose: RegisterChange[] = [];
  for (const c of changes) {
    const g = c.merge_id ? at.get(c.merge_id) : undefined;
    if (g) g.changes.push(c);
    else loose.push(c);
  }
  return loose.length ? [...groups, { merge: null, changes: loose }] : groups;
}

/** A release's changes in the order the lists show them: by merge when it has merges. */
export function inOrder(changes: RegisterChange[], merges: RegisterMerge[]): RegisterChange[] {
  return merges.length ? byMerge(changes, merges).flatMap((g) => g.changes) : byScreen(changes).flatMap((g) => g.changes);
}

/** One entry in the lists: a change, or a bundle of path changes checked together. */
export type Item =
  | { kind: 'change'; id: string; change: RegisterChange; changes: RegisterChange[] }
  | { kind: 'bundle'; id: string; key: string; title: string; changes: RegisterChange[] };

/** Changes as list entries, in order: two or more sharing a bundle become one entry where the first stands. */
export function itemsOf(changes: RegisterChange[]): Item[] {
  const members = new Map<string, RegisterChange[]>();
  for (const c of changes) if (c.bundle_key) members.set(c.bundle_key, [...(members.get(c.bundle_key) ?? []), c]);
  const out: Item[] = [];
  const placed = new Set<string>();
  for (const c of changes) {
    const group = c.bundle_key ? members.get(c.bundle_key)! : [];
    if (group.length < 2) { out.push({ kind: 'change', id: c.id, change: c, changes: [c] }); continue; }
    if (placed.has(c.bundle_key!)) continue;
    placed.add(c.bundle_key!);
    out.push({ kind: 'bundle', id: `b:${c.bundle_key}`, key: c.bundle_key!, title: c.bundle_title || 'Paths changed', changes: group });
  }
  return out;
}

/** A bundle needs checking while any of its changes does; it is done when all are. */
export const itemNeedsCheck = (i: Item) => i.changes.some((c) => c.status === 'open' || c.status === 'conflict');
export const itemDone = (i: Item) => i.changes.every((c) => c.status === 'accepted' || c.status === 'rejected' || c.status === 'applied');

/** "Home 2.54.0" and, while it is coming, "not released yet". */
export const releaseState = (r: RegisterRelease) => (r.released ? r.tag ?? '' : 'Not released yet');

/** A one-line hint under a change in the lists. */
export function changeHint(c: RegisterChange): string {
  if (c.status === 'conflict') return 'Changed in the sheet since';
  if (c.kind === 'retire') return 'To retire';
  if (c.kind === 'new') return 'Not in the register yet';
  const n = c.changes.length;
  return `${n} field${n === 1 ? '' : 's'}`;
}
